import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Image as ImageIcon, Loader2, Trash2, UploadCloud } from 'lucide-react';
import { resolveMediaUrl } from '@lms/shared/utils';
import { apiClient, apiBaseUrl } from '../lib/api-client';

interface ProgramCoverFieldProps {
  id: string;
  value: string;
  programName: string;
  onChange: (value: string) => void;
  label: ReactNode;
  uploadPrompt: ReactNode;
  recommendation: ReactNode;
  uploadingLabel: ReactNode;
  changeLabel: ReactNode;
  removeLabel: ReactNode;
  previewUnavailable: ReactNode;
}

export function ProgramCoverField({
  id,
  value,
  programName,
  onChange,
  label,
  uploadPrompt,
  recommendation,
  uploadingLabel,
  changeLabel,
  removeLabel,
  previewUnavailable,
}: ProgramCoverFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadIdRef = useRef(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const currentCover = previewUrl || resolveMediaUrl(value, apiBaseUrl);
  const coverFailedToLoad = currentCover !== undefined && failedSource === currentCover;

  useEffect(
    () => () => {
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFileUpload = async (file: File) => {
    const uploadId = uploadIdRef.current + 1;
    uploadIdRef.current = uploadId;
    setPreviewUrl(URL.createObjectURL(file));
    setFailedSource(null);
    setUploadError(null);
    setIsUploading(true);

    try {
      const response = await apiClient.storage.upload(file);
      if (uploadId === uploadIdRef.current) {
        onChange(response.url);
      }
    } catch (error) {
      if (uploadId === uploadIdRef.current) {
        setUploadError(error instanceof Error ? error.message : 'Gagal mengunggah gambar');
        setPreviewUrl(null);
      }
    } finally {
      if (uploadId === uploadIdRef.current) {
        setIsUploading(false);
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      void handleFileUpload(file);
    }
  };

  const handleRemove = () => {
    uploadIdRef.current += 1;
    setPreviewUrl(null);
    setFailedSource(null);
    setUploadError(null);
    setIsUploading(false);
    onChange('');
  };

  return (
    <section className="space-y-3" aria-labelledby={`${id}-label`}>
      <div className="space-y-1">
        <p
          id={`${id}-label`}
          className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
        >
          {label}
        </p>
        <p className="px-2 text-[11px] font-body text-on-surface-variant/70">{recommendation}</p>
      </div>

      <div className="rounded-2xl bg-surface-container-low p-3 sm:p-4">
        {currentCover && !coverFailedToLoad ? (
          <div className="relative aspect-video max-h-56 w-full overflow-hidden rounded-xl bg-surface-container">
            <img
              src={currentCover}
              alt={programName}
              className="h-full w-full object-cover"
              onError={() => setFailedSource(currentCover)}
            />
            {isUploading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-on-surface/70 backdrop-blur-sm">
                <Loader2 className="size-7 animate-spin text-on-primary" />
                <span className="text-xs font-bold text-on-primary">{uploadingLabel}</span>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={openFilePicker}
            disabled={isUploading}
            className="flex min-h-44 w-full flex-col items-center justify-center gap-3 rounded-xl bg-surface-container px-5 py-6 text-center transition-colors hover:bg-surface-container-high focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait"
          >
            {isUploading ? (
              <>
                <Loader2 className="size-8 animate-spin text-primary" />
                <span className="text-xs font-bold text-primary">{uploadingLabel}</span>
              </>
            ) : (
              <>
                <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <ImageIcon size={24} />
                </span>
                <span className="space-y-1">
                  <span className="block text-xs font-bold text-on-surface">{uploadPrompt}</span>
                  {coverFailedToLoad && (
                    <span className="block text-[11px] font-medium text-error">
                      {previewUnavailable}
                    </span>
                  )}
                </span>
              </>
            )}
          </button>
        )}

        {currentCover && !coverFailedToLoad && !isUploading && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openFilePicker}
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-surface-container-high px-4 text-xs font-bold text-on-surface transition-colors hover:bg-surface-container-highest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <UploadCloud size={15} />
              {changeLabel}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-error-container px-4 text-xs font-bold text-on-error-container transition-colors hover:bg-error-container/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error"
            >
              <Trash2 size={15} />
              {removeLabel}
            </button>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        id={id}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />

      {uploadError && <p className="px-2 text-xs font-medium text-error">{uploadError}</p>}
    </section>
  );
}
